// Versão web instalável no celular (AC-251): o service worker e o manifesto deixam o
// app.beedoit.app ser adicionado à tela inicial e abrir como app, em tela cheia. No Android o
// navegador oferece a instalação (beforeinstallprompt) e o convite mostra "Instalar"; no iPhone a
// instalação é pelo menu Compartilhar, então o convite explica o caminho. Instalado, nada aparece.

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallMode = 'prompt' | 'ios' | null;

const DISMISSED_KEY = 'bdi-install-dismissed';

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** Registra o service worker (só em https) e guarda o pedido de instalação do navegador. */
export function initPwa(win: Window = window): void {
  const nav = win.navigator;
  if ('serviceWorker' in nav && win.location.protocol === 'https:') {
    nav.serviceWorker.register('/sw.js').catch(() => undefined);
  }
  win.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // o convite do app decide quando oferecer
    deferred = e as InstallPromptEvent;
    notify();
  });
  win.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

export function onInstallChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

/** Já está aberto como app instalado (tela cheia)? */
export function isStandalone(win: Window = window): boolean {
  return win.matchMedia?.('(display-mode: standalone)').matches || (win.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** iPhone ou iPad (o iPad recente se apresenta como Mac com toque). */
export function isIos(ua: string, touchPoints = 0): boolean {
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);
}

/** O que o convite deve mostrar: o botão "Instalar", o caminho no iPhone, ou nada. */
export function installMode(win: Window = window, hasPrompt = deferred !== null): InstallMode {
  if (isStandalone(win)) return null;
  if (hasPrompt) return 'prompt';
  return isIos(win.navigator.userAgent, win.navigator.maxTouchPoints) ? 'ios' : null;
}

/** Abre a instalação do navegador; true se a pessoa aceitou. */
export async function promptInstall(): Promise<boolean> {
  const event = deferred;
  if (!event) return false;
  deferred = null;
  notify();
  await event.prompt();
  return (await event.userChoice).outcome === 'accepted';
}

export function installDismissed(storage: Pick<Storage, 'getItem'> | undefined): boolean {
  try {
    return storage?.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissInstall(storage: Pick<Storage, 'setItem'> | undefined): void {
  try {
    storage?.setItem(DISMISSED_KEY, '1');
  } catch {
    /* sem armazenamento: o convite volta na próxima visita */
  }
}
